import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { PdfUpload } from "@/components/PdfUpload";
import { toast } from "sonner";
import { Loader2, Check } from "lucide-react";
import { LOGO_URL } from "@/lib/brand";

const STEPS = ["Profile Info", "Upload W-9", "Signed NDA"];

export default function Onboarding() {
  const { user, refresh, loading } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ phone: "", address: "", business_name: "" });
  const [w9, setW9] = useState(null);
  const [nda, setNda] = useState(null);
  const [expertiseOptions, setExpertiseOptions] = useState([]);
  const [selectedExpertise, setSelectedExpertise] = useState([]);
  const [otherText, setOtherText] = useState("");

  useEffect(() => { api.get("/expertise").then(({ data }) => setExpertiseOptions(data.expertise)).catch(() => {}); }, []);

  const toggleExpertise = (name) => setSelectedExpertise((p) => p.includes(name) ? p.filter((x) => x !== name) : [...p, name]);
  const hasOther = selectedExpertise.includes("Other");

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login"); return; }
    if (user.role === "admin") navigate("/admin");
    else if (user.status === "approved") navigate("/portal");
    else if (user.status !== "pending_onboarding") navigate("/pending");
  }, [user, loading, navigate]);

  const inputCls = "w-full bg-[#050505] border border-[#2A2A2A] px-4 py-3 text-white text-sm focus:outline-none focus:border-[#FF4D00] transition-colors placeholder:text-[#555]";

  const next = () => {
    if (step === 0 && (!form.phone || !form.address || !form.business_name)) { toast.error("Fill in all profile fields"); return; }
    if (step === 0 && selectedExpertise.length === 0) { toast.error("Select at least one Area of Expertise"); return; }
    if (step === 0 && hasOther && !otherText.trim()) { toast.error("Please describe your 'Other' specialty"); return; }
    if (step === 1 && !w9) { toast.error("Upload your W-9 PDF"); return; }
    setStep((s) => s + 1);
  };

  const submit = async () => {
    if (!nda) { toast.error("Upload your signed NDA PDF"); return; }
    setSubmitting(true);
    try {
      await api.post("/onboarding", { ...form, w9_file_id: w9.id, nda_file_id: nda.id, expertise: selectedExpertise, expertise_other: hasOther ? otherText.trim() : "" });
      await refresh();
      toast.success("Onboarding submitted for review");
      navigate("/pending");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white grid-noise">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <img src={LOGO_URL} alt="Splash Creative Hub" className="w-[150px] h-auto object-contain" />
        <div className="text-xs uppercase tracking-[0.25em] text-[#FF4D00] mt-10 mb-3">Onboarding Wizard</div>
        <h1 className="font-bebas text-5xl mb-2">GET APPROVED TO WORK</h1>
        <p className="text-sm text-[#A1A1AA] mb-10">Complete all three steps. An admin will review and approve your access.</p>

        {/* Stepper */}
        <div className="flex items-center mb-12">
          {STEPS.map((label, i) => (
            <div key={i} className="flex items-center flex-1 last:flex-none">
              <div className="flex items-center gap-3">
                <div className={`w-9 h-9 flex items-center justify-center border text-sm ${i < step ? "bg-[#10B981] border-[#10B981] text-black" : i === step ? "bg-[#FF4D00] border-[#FF4D00] text-white" : "border-[#2A2A2A] text-[#A1A1AA]"}`}>
                  {i < step ? <Check className="w-4 h-4" /> : i + 1}
                </div>
                <span className={`text-xs uppercase tracking-[0.12em] hidden sm:block ${i <= step ? "text-white" : "text-[#A1A1AA]"}`}>{label}</span>
              </div>
              {i < STEPS.length - 1 && <div className={`h-px flex-1 mx-3 ${i < step ? "bg-[#10B981]" : "bg-[#2A2A2A]"}`} />}
            </div>
          ))}
        </div>

        <div className="border border-[#2A2A2A] bg-[#111111] p-8">
          {step === 0 && (
            <div className="space-y-4" data-testid="onboarding-step-profile">
              <h3 className="font-bebas text-2xl tracking-wide mb-2">PROFILE INFORMATION</h3>
              <input data-testid="onboarding-business-input" className={inputCls} placeholder="Business / legal name" value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} />
              <input data-testid="onboarding-phone-input" className={inputCls} placeholder="Phone number" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              <textarea data-testid="onboarding-address-input" className={`${inputCls} min-h-[100px]`} placeholder="Mailing address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] block mb-2">Areas of Expertise</label>
                <div className="flex flex-wrap gap-2" data-testid="expertise-options">
                  {[...expertiseOptions.map((o) => o.name), "Other"].map((name) => (
                    <button type="button" key={name} data-testid={`expertise-${name}`} onClick={() => toggleExpertise(name)} className={`px-3 py-2 text-sm border transition-colors ${selectedExpertise.includes(name) ? "border-[#FF4D00] bg-[#FF4D0020] text-white" : "border-[#2A2A2A] text-[#A1A1AA] hover:border-[#FF4D00]"}`}>{name}</button>
                  ))}
                </div>
                {hasOther && (
                  <input data-testid="expertise-other-input" className={`${inputCls} mt-3`} placeholder="Tell us your specialty…" value={otherText} onChange={(e) => setOtherText(e.target.value)} />
                )}
              </div>
            </div>
          )}
          {step === 1 && (
            <div className="space-y-4" data-testid="onboarding-step-w9">
              <h3 className="font-bebas text-2xl tracking-wide mb-2">UPLOAD YOUR W-9</h3>
              <p className="text-sm text-[#A1A1AA]">Provide a completed IRS Form W-9 as a PDF.</p>
              <PdfUpload testId="w9-upload" category="w9" value={w9} onUploaded={setW9} label="W-9 Form (PDF)" />
            </div>
          )}
          {step === 2 && (
            <div className="space-y-4" data-testid="onboarding-step-nda">
              <h3 className="font-bebas text-2xl tracking-wide mb-2">UPLOAD SIGNED NDA</h3>
              <p className="text-sm text-[#A1A1AA]">Provide your signed Non-Disclosure Agreement as a PDF.</p>
              <PdfUpload testId="nda-upload" category="nda" value={nda} onUploaded={setNda} label="Signed NDA (PDF)" />
            </div>
          )}

          <div className="flex justify-between mt-8 pt-6 border-t border-[#2A2A2A]">
            <button data-testid="onboarding-back-btn" disabled={step === 0} onClick={() => setStep((s) => s - 1)} className="border border-[#2A2A2A] px-6 py-3 text-sm hover:bg-[#1A1A1A] transition-colors disabled:opacity-40">Back</button>
            {step < STEPS.length - 1 ? (
              <button data-testid="onboarding-next-btn" onClick={next} className="bg-[#FF4D00] text-white px-6 py-3 text-sm hover:bg-[#E64500] transition-colors">Continue</button>
            ) : (
              <button data-testid="onboarding-submit-btn" onClick={submit} disabled={submitting} className="bg-[#FF4D00] text-white px-6 py-3 text-sm hover:bg-[#E64500] transition-colors flex items-center gap-2 disabled:opacity-60">
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />} Submit for review
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
